import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

import type { AugmentedShipment } from "./use-get-shipment.ts";

// TODO: Replace with the real response schema once the shipment add-insurance
// endpoint is implemented on the backend. For now we only need the resulting
// insurance total to reflect the purchase in the Insurance card.
const addShipmentInsuranceResponseSchema = z.object({
	totalAmount: z.number().nullable(),
});

export type AddShipmentInsuranceResponse = z.infer<
	typeof addShipmentInsuranceResponseSchema
>;

export type AddShipmentInsurancePayload = {
	poNumber: number;
	quoteId: number;
	insuranceValue: number;
	insuranceApplyTypeId: number;
	insuranceProductTypeId: number;
};

export const addShipmentInsurance = async (
	payload: AddShipmentInsurancePayload,
	isInternal: boolean,
): Promise<AddShipmentInsuranceResponse> => {
	const route = isInternal
		? apiRoutes.ADDSHIPMENTINSURANCE
		: apiRoutes.ADDCUSTOMERSHIPMENTINSURANCE;

	const url = route.replace(":poNumber", String(payload.poNumber));

	const resp = await postAndValidate(
		url,
		payload,
		addShipmentInsuranceResponseSchema,
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const useAddShipmentInsurance = () => {
	const queryClient = useQueryClient();
	const { isInternal } = useDeploymentType();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutateAsync, isPending, error, reset } = useMutation<
		AddShipmentInsuranceResponse,
		Error,
		AddShipmentInsurancePayload
	>({
		mutationFn: (payload) => addShipmentInsurance(payload, isInternal),
		onSuccess: (data) => {
			queryClient.setQueriesData<AugmentedShipment>(
				{ queryKey: ["shipment"] },
				(old) =>
					old
						? {
								...old,
								insurance: { ...old.insurance, totalAmount: data.totalAmount },
							}
						: old,
			);
			showSuccess("Insurance added.");
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to add insurance. Please try again.",
			);
		},
	});

	return {
		addShipmentInsurance: mutateAsync,
		isSavingInsurance: isPending,
		error,
		resetError: reset,
	};
};
